import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Lock, X, KeyRound, ShieldCheck, Send, CheckCircle2, RotateCcw, Undo2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { StarField } from "./Decorations";
import { GROUPS, PANITIA_PASSWORD } from "../data/groups";

const API = "/api";

const ClueCard = ({ g, info, clue, answer, submitting, isPanitia, onChange, onSubmit, onUnlock, delay }) => {
  const submitted = !!info?.submitted;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`clue-card-${g.codename.toLowerCase()}`}
      className={`relative overflow-hidden rounded-3xl border p-5 transition-colors duration-500 ${
        submitted ? `border-white/30 bg-gradient-to-br ${g.gradient}` : "border-white/10 bg-white/[0.04]"
      }`}
      style={submitted ? { boxShadow: `0 0 32px ${g.glow}44` } : {}}
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-white/60">{g.name}</p>
          <h3 className="font-heading text-lg font-semibold leading-tight text-white">{g.codename}</h3>
        </div>
        <span
          data-testid={`clue-status-${g.codename.toLowerCase()}`}
          className={`flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-widest ${
            submitted ? "bg-white/25 text-white" : "bg-white/10 text-white/50"
          }`}
        >
          {submitted ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Lock className="h-3 w-3" />}
          {submitted ? "Sudah submit" : "Belum submit"}
        </span>
      </div>

      {clue ? (
        <p
          data-testid={`clue-text-${g.codename.toLowerCase()}`}
          className="mt-3 rounded-2xl border border-[#4cc9f0]/25 bg-white/[0.05] px-4 py-2.5 text-xs leading-relaxed text-white/75"
        >
          <span className="font-semibold text-[#4cc9f0]">Clue Tempat: </span>
          {clue}
        </p>
      ) : null}

      <div className="mt-4">
        {submitted ? (
          <div className="flex items-center justify-between gap-2 rounded-2xl border border-white/20 bg-white/10 px-4 py-3">
            <p className="text-xs leading-relaxed text-white/85">Jawaban tercatat oleh panitia. Kolom terkunci.</p>
            {isPanitia && (
              <button
                onClick={() => onUnlock(g.codename)}
                data-testid={`clue-unlock-${g.codename.toLowerCase()}`}
                className="flex shrink-0 items-center gap-1 rounded-full bg-white/20 px-3 py-1.5 text-[11px] font-medium text-white transition-colors hover:bg-white/30"
              >
                <Undo2 className="h-3 w-3" /> Buka ulang
              </button>
            )}
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit(g.codename);
            }}
            className="flex items-center gap-2"
          >
            <input
              value={answer}
              onChange={(e) => onChange(g.codename, e.target.value)}
              placeholder="Tulis jawaban clue..."
              data-testid={`clue-input-${g.codename.toLowerCase()}`}
              className="w-full rounded-full border border-white/20 bg-white/10 px-4 py-2.5 text-sm text-white placeholder:text-white/40 outline-none transition-colors focus:border-[#4cc9f0]"
            />
            <button
              type="submit"
              disabled={submitting}
              data-testid={`clue-submit-${g.codename.toLowerCase()}`}
              aria-label={`Submit jawaban ${g.codename}`}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#4cc9f0] text-[#0A0A1A] transition-transform duration-300 hover:scale-105 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        )}
      </div>
    </motion.div>
  );
};

export const ClueBoard = () => {
  const [status, setStatus] = useState([]);
  const [clueCfg, setClueCfg] = useState({});
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [pw, setPw] = useState("");
  const [pwError, setPwError] = useState(false);
  const [isPanitia, setIsPanitia] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        axios.get(`${API}/clues/status`),
        axios.get(`${API}/clues/config`),
      ]);
      setStatus(s.data);
      const cmap = {};
      (c.data || []).forEach((x) => (cmap[x.group] = x.clue));
      setClueCfg(cmap);
    } catch {
      // status board gagal dimuat; coba lagi di polling berikutnya
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 10000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const submittedCount = status.filter((s) => s.submitted).length;

  const onChange = (group, value) => setAnswers((p) => ({ ...p, [group]: value }));

  const onSubmit = async (group) => {
    const answer = (answers[group] || "").trim();
    if (!answer || submitting) return;
    setSubmitting(group);
    try {
      await axios.post(`${API}/clues/submit`, { group, answer });
      toast.success(`${group} berhasil submit clue!`);
      setAnswers((p) => ({ ...p, [group]: "" }));
      await fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal submit. Coba lagi.");
    } finally {
      setSubmitting(null);
    }
  };

  const submitPw = (e) => {
    e.preventDefault();
    if (pw.trim() === PANITIA_PASSWORD) {
      setIsPanitia(true);
      setShowPw(false);
      setPw("");
      setPwError(false);
    } else {
      setPwError(true);
      setTimeout(() => setPwError(false), 2500);
    }
  };

  const resetAll = async () => {
    try {
      await axios.post(`${API}/clues/reset`, { password: PANITIA_PASSWORD });
      toast("Papan clue direset — siap untuk minggu baru.");
      await fetchStatus();
    } catch {
      toast.error("Gagal mereset papan.");
    }
  };

  const unlockGroup = async (group) => {
    try {
      await axios.post(`${API}/clues/unlock`, { password: PANITIA_PASSWORD, group });
      toast(`Status ${group} dibuka ulang.`);
      await fetchStatus();
    } catch {
      toast.error("Gagal membuka ulang status.");
    }
  };

  return (
    <section
      id="papan-clue"
      data-testid="clue-board-section"
      className="relative overflow-hidden bg-gradient-to-b from-[#191345] via-[#221756] to-[#191345] px-6 py-24 sm:py-32"
    >
      <StarField count={45} />

      {/* Akses panitia */}
      <div className="absolute right-4 top-4 z-20">
        {!isPanitia && (
          <button
            onClick={() => setShowPw((v) => !v)}
            data-testid="clue-panitia-button"
            aria-label="Akses panitia"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/40 transition-colors duration-300 hover:border-white/30 hover:text-white/80"
          >
            <Lock className="h-4 w-4" />
          </button>
        )}

        <AnimatePresence>
          {showPw && !isPanitia && (
            <motion.form
              onSubmit={submitPw}
              initial={{ opacity: 0, y: -8, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.97 }}
              transition={{ duration: 0.2 }}
              data-testid="clue-panitia-form"
              className="glass absolute right-0 mt-2 w-64 rounded-2xl p-4 shadow-2xl"
            >
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-widest text-white/70">
                  <ShieldCheck className="h-3.5 w-3.5 text-[#4cc9f0]" /> Akses Panitia
                </span>
                <button type="button" onClick={() => setShowPw(false)} aria-label="Tutup" className="text-white/40 hover:text-white">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="relative mt-3">
                <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                <input
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="Password panitia"
                  autoFocus
                  data-testid="clue-panitia-input"
                  className={`w-full rounded-full border bg-white/10 py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-white/40 outline-none transition-colors focus:border-[#4cc9f0] ${
                    pwError ? "border-[#f72585]" : "border-white/20"
                  }`}
                />
              </div>
              <button
                type="submit"
                data-testid="clue-panitia-submit"
                className="mt-3 w-full rounded-full bg-[#4cc9f0] py-2.5 font-heading text-sm font-medium text-[#0A0A1A] transition-transform duration-300 hover:scale-[1.02]"
              >
                Masuk
              </button>
              <AnimatePresence>
                {pwError && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    data-testid="clue-panitia-error"
                    className="mt-2 flex items-center gap-1.5 text-xs font-medium text-[#f72585]"
                  >
                    <X className="h-3.5 w-3.5" /> Password salah.
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.form>
          )}
        </AnimatePresence>

        {isPanitia && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="clue-panitia-panel"
            className="glass flex items-center gap-2 rounded-full px-4 py-2 shadow-2xl"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-[#4cc9f0]" />
            <span className="text-[11px] font-medium uppercase tracking-widest text-white/70">Panitia</span>
            <button
              onClick={resetAll}
              data-testid="clue-reset-button"
              className="flex items-center gap-1.5 rounded-full bg-[#f72585] px-3 py-1.5 text-[11px] font-medium text-white transition-transform duration-300 hover:scale-105"
            >
              <RotateCcw className="h-3 w-3" /> Reset Minggu Baru
            </button>
          </motion.div>
        )}
      </div>

      <div className="relative z-10 mx-auto max-w-6xl">
        <Reveal>
          <p className="mb-4 text-sm font-medium uppercase tracking-[0.3em] text-[#4cc9f0]">Misi Mingguan</p>
          <h2 className="font-heading text-3xl font-medium leading-tight tracking-tight sm:text-4xl md:text-5xl">
            Papan <span className="aurora-text">Status Clue</span>
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/75">
            Setiap kelompok menuliskan jawaban clue yang sudah ditemukan di lapangan. Begitu ketujuh
            kelompok submit, input password gerbang aktif di Kunci Orbit terbuka otomatis.
          </p>
          <p
            data-testid="clue-progress"
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-4 py-1.5 text-xs uppercase tracking-widest text-white/70"
          >
            <CheckCircle2 className="h-3.5 w-3.5 text-[#4cc9f0]" />
            {submittedCount} / {GROUPS.length} kelompok sudah submit
          </p>
        </Reveal>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="clue-grid">
          {GROUPS.map((g, i) => (
            <ClueCard
              key={g.codename}
              g={g}
              info={status.find((s) => s.group === g.codename)}
              clue={clueCfg[g.codename] || ""}
              answer={answers[g.codename] || ""}
              submitting={submitting === g.codename}
              isPanitia={isPanitia}
              onChange={onChange}
              onSubmit={onSubmit}
              onUnlock={unlockGroup}
              delay={i * 0.06}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
